import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import {
  AnimatedSpinner,
  Download,
} from '@unified-checkout/design-system/icons/index';
import { DataTable } from '@unified-checkout/design-system';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  getStatementsOrDigestList,
  downloadStatementsOrDigest,
} from '@app/dashboard/services/inner_layout/reports';
import type {
  TDigestFileType,
  TStatement,
} from '@app/dashboard/types/innerLayout/reports';
import { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES
} from '@app/dashboard/constants/tracking_events/system';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { extractDownloadPath } from '@app/dashboard/views/pages/inner_layout/Reports/utils/extractDownloadPath';
import { notify } from '@app/dashboard/utils/notifier';
import { downloadFile } from '@app/dashboard/utils/downloadFile';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import styles from './digest-tab.module.scss';

export const DigestTab = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [downloading, setDownloading] = useState<{
    id: number;
    type: TDigestFileType;
  } | null>(null);

  const downloadMutation = useMutation({
    mutationFn: ({
      statement,
      type,
    }: {
      statement: TStatement;
      type: TDigestFileType;
    }) => {
      const fullUrl = statement.pdf_url;
      const downloadPath = extractDownloadPath(fullUrl);
      return downloadStatementsOrDigest({ downloadPath });
    },
    onSuccess: (res, variables) => {
      const { type, statement } = variables;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_SUCCEEDED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]:
          REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.DIGEST,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]:
          REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.PDF,
        [SHARED_EVENTS_PARAMS.DATE_RANGE]: statement.title,
      });
      downloadFile({ res, type, defaultFilename: 'digest' });
      setDownloading(null);
    },
    onError: (error: any) => {
      const errorDescription =
        error?.response?.data?.message ||
        error?.message || 
        t('errors.somethingWentWrong');

      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_FAILED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]:
          REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.DIGEST,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.ERROR_DESCRIPTION]: errorDescription,
      });

      setDownloading(null);
      notify.error(errorDescription, {
        timeout: 2,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REPORTS.DIGEST_TAB.DOWNLOAD_DIGEST,
            msg: errorDescription,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REPORTS.DIGEST_TAB,
          }),
      });
    },
  });

  const handleDownload = (
    statement: TStatement,
    type: TDigestFileType
  ) => {
    setDownloading({ id: statement.id, type });
    downloadMutation.mutate({ statement, type });
  };

  const columnsMap = [
    {
      id: 'date',
      width: '90%',
      name: t('innerLayout.reportsPage.digestTab.date'),
      type: {
        conditionsType: 'direct_value',
        conditions: {
          leading_text: { is_new: true },
        },
        default: 'text',
      },
      key: 'title',
      badgeProps: (_row: TStatement, rowIndex: number) => {
        return rowIndex === 0
          ? {
              color: 'green',
              text: t('innerLayout.reportsPage.statementsTable.badges.new'),
              size: 'sm',
            }
          : undefined;
      },
    },
    {
      id: 'pdf',
      name: '',
      type: 'action_btns',
      actionBtnsProps: (row: TStatement) => [
        {
          variant: 'outlined',
          text: t('innerLayout.download'),
          leadingIcon: downloadMutation.isPending && downloading?.id === row.id ? AnimatedSpinner : Download,
          disabled: !row.is_active || (
            downloadMutation.isPending && downloading
          ),
          color: 'secondary',
          onClickHandler: () => handleDownload(row, 'pdf'),
        },
      ],
    },
  ];

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_DIGEST, isLive],
    [isLive]
  );
 
  const queryFunctionParams = useMemo(
    () => ({ is_live: isLive, platform: 'dashboard', service: 'Digest' }),
    [isLive]
  );

  return (
    <div className={styles['digest-wrapper']}>
      <div className={styles['digest-wrapper__digest-header']}>
        <div className={styles['digest-wrapper__digest-header__title']}>{t('innerLayout.reportsPage.digestTab.title')}</div>
        <div className={styles['digest-wrapper__digest-header__desc']}>
          {t('innerLayout.reportsPage.digestTab.description')}
        </div>
      </div>
      <div className={styles['statements-tab-wrapper__notification-wrapper']}>
      
      </div>
      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          tableId: 'digest_table',
          dataLocation: 'data.data',
          columnsMap: columnsMap as IColumnsMap,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getStatementsOrDigestList,
          queryFunctionParams: queryFunctionParams,
        }}
        pagination={{
          type: 'cursor_based',
          dataLocation: {
            next: 'next',
            prev: 'previous',
          },
          hideLabels: isMobile,
        }}
        emptyState={{
          text: t('innerLayout.reportsPage.digestTab.body.noDigestYet'),
          description: t('innerLayout.reportsPage.digestTab.body.noDigestHint'),
        }}
      />
    </div>
  );
};
